import {DtConstants} from '../../schema/DtConstants';
import {doEvalSimpleState, intoStreamKey} from '../StreamUtils';

const MIN = 60;
const DAY = 24 * 60 * MIN;

const {StreamStatePriority, StreamStates} = DtConstants;

export class StatusIntervalTree {
	constructor(nodes, tree = new UnbalancedBinTree()) {
		const stops = new Set();
		shuffle(nodes); // Shuffling to limit occurrence of degenerate trees.
		for (const node of nodes) {
			tree.add(node);
			stops.add(node.low);
			stops.add(node.high);
		}

		this.tree = tree;
		// Possible to optimize by keeping more information on which stops belong to which EIDs/AIDs
		this.stops = stops;
	}

	// Chronological array of OOC regions.
	collapseBy(filter) {
		let collapsed = [];
		if (!this.stops.size) return collapsed;

		// Prepare filter.
		let eids, aids;
		if (filter?.elementIds) {
			eids = new Set(filter.elementIds);
		}
		if (filter?.attrIds) {
			aids = new Set(filter.attrIds);
		}
		const match = ({eid, aid}) => (!eids || eids.has(eid)) && (!aids || aids.has(aid));

		// For every change in interval tree
		const ordered = [...this.stops].sort((a, b) => a - b);
		let region = {};
		const endRegion = (s) => {
			region.high = s;
			collapsed.push(region);
			region = {};
		}
		const startRegion = (s, worst, withWorst) => {
			region.low = s;
			region.active = withWorst;
			region.status = worst;
		}
		for (const s of ordered) {
			// Find intervals active as stop, that matches the filter.
			const actives = this.query(s).filter(match);

			// Get the worst / highest priority status.
			let worst;
			if (actives.length) {
				// Could be optimized if we hard code the only two possible state for now.
				worst = actives[0].status;
				for (let i = 1; i < actives.length; i++) {
					const st = actives[i].status;
					if (StreamStatePriority[st] > StreamStatePriority[worst]) {
						worst = st;
					}
				}
			}

			if (!region.status && !worst) {
				continue; // Possible in filtered scenario, we are not in an active status, and are not getting into one.
			}

			if (region.status && !worst) {
				// Any active interval is ending.
				endRegion(s);
				continue;
			}

			let withWorst = new Set();
			for (const n of actives) {
				if (n.status !== worst) continue;

				withWorst.add(intoStreamKey(n.eid, n.aid));
			}

			if (!region.status && worst) {
				startRegion(s, worst, withWorst);
				continue;
			}

			// So here is an important design decision being made. By including information about which streams
			// are out-of-compliance in each region, we have to split region of common status everytime a stream joins or drops out.
			// We could completely ignore the case where status === worst, if we didn't guarantee a set of streams.
			// if (status === worst)

			if (eqSets(withWorst, region.active) && region.status === worst) {
				// case where there was a change in lower priority streams, irrelevant to our current section.
				continue;
			}

			// The last severity has ended, start a new one.
			endRegion(s);
			startRegion(s, worst, withWorst);
		}

		return collapsed;
	}

	// Get active (overlapping) intervals at a point.
	query(pt) {
		const nodes = [];

		function search(node) {
			if (!node) return;
			if (IntervalNode.includes(node, pt)) nodes.push(node);
			if (node.left && IntervalNode.max(node.left) > pt) search(node.left);
			if (node.right && IntervalNode.min(node.right) <= pt) search(node.right);
		}

		search(this.tree.root);
		return nodes;
	}

	refreshStream(eid, aid, data, metadata, threshold, timeout, intervalBeg, intervalEnd) {
		// refreshes by collecting nodes referencing the stream, computing the new required nodes and adapting the tree.

		const toRemove = new Map();
		this.tree.inOrder((n) => {
			if (n.eid === eid && n.aid === aid) {
				toRemove.set(n.hash(), n);
			}
		});

		// Go through new nodes and collect what needs changing.
		const {minIdx, maxIdx} = metadata;
		const toAdd = new Map();
		const addRange = (beg, end, status) => {
			if (status === StreamStates.Normal) return;
			const node = new IntervalNode(eid, aid, beg, end, status);
			const hash = node.hash();
			if (toRemove.has(hash)) {
				toRemove.delete(hash);
			} else {
				toAdd.set(hash, node);
			}
		}
		const ssm = new StreamStateMachine();
		ssm.start(threshold, timeout, addRange, intervalBeg, intervalEnd);
		for (let i = minIdx; i <= maxIdx; i++) ssm.processDay(data[i]);
		ssm.end();

		// Update the tree based on the observed difference.
		for (const node of toRemove.values()) {
			this.tree.delete(node);
			// doesn't clean stops as they are not 1:1 with nodes.
		}

		for (const node of toAdd.values()) {
			this.tree.add(node);
			this.stops.add(node.low);
			this.stops.add(node.high);
		}
	}

	static from(data, idx, thresholds, timeouts, intervalBeg, intervalEnd) {
		//console.time('StatusIntervalTree<from>');
		// To note, this considers data as passed-in. It does not do any special processing for outliers > 1 day old.
		const nodes = [];
		const ssm = new StreamStateMachine();
		for (const eid in idx) {
			if (thresholds[eid] == null) continue;

			for (const aid in idx[eid]) {
				if (thresholds[eid][aid] == null) continue;

				const {minIdx, maxIdx} = idx[eid][aid];
				const threshold = thresholds[eid][aid];
				const timeout = timeouts[eid];
				const addRange = (beg, end, status) => {
					if (status === StreamStates.Normal) return;
					nodes.push(new IntervalNode(eid, aid, beg, end, status));// We skip undesired normal status, because we don't show it anyway.
				}

				ssm.start(threshold, timeout, addRange, intervalBeg, intervalEnd);
				for (let i = minIdx; i <= maxIdx; i++) {
					const row = data[i];
					ssm.processDay(row);
				}
				ssm.end();
			}
		}

		const tree = new StatusIntervalTree(nodes);
		//console.timeEnd('StatusIntervalTree<from>');
		return tree;
	}

	/** Converts a js object back into an instance of this class. */
	static rehydrate(o) {
		Object.setPrototypeOf(o, StatusIntervalTree.prototype);
		Object.setPrototypeOf(o.tree, UnbalancedBinTree.prototype);
		return o;
	}
}

// Knuth shuffle
function shuffle(arr) {
	// Going from right to left shuffling in the remaining left elements.
	let idx = arr.length;
	while (idx !== 0) {
		const swapWith = Math.floor(Math.random() * idx--);
		const tmp = arr[idx];
		arr[idx] = arr[swapWith];
		arr[swapWith] = tmp;
	}
}

/** Assumes lhs and rhs are valid ES6 Sets. */
const eqSets = (a, b) => {
	if (a.size !== b.size) return false;
	for (const v of a.values()) {
		if (!b.has(v)) return false;
	}
	return true;
}

export class IntervalNode {
	left;
	right;
	parent;

	// Baked augmentation that means it can only be queried once the tree is complete.
	/** @private Maximum of the tree under this node. */
	max;
	/** @private Minimum of the tree under this node. */
	min;

	constructor(eid, aid, low, high, status) {
		// Key used by the tree.
		this.key = low;

		this.eid = eid;
		this.aid = aid;
		this.low = low;
		this.high = high;
		this.status = status;
	}

	static includes(node, pt) {
		return pt < node.high && pt >= node.low;
	}

	static max(node) {
		if (node == null) return -Infinity;
		if (node.max == null) {
			node.max = Math.max(node.high, IntervalNode.max(node.left), IntervalNode.max(node.right));
		}
		return node.max;
	}

	static min(node) {
		if (node == null) return Infinity;
		if (node.min == null) {
			node.min = Math.min(node.low, IntervalNode.min(node.left), IntervalNode.min(node.right));
		}
		return node.min;
	}

	static height(node) {
		return node
			? 1 + Math.max(IntervalNode.height(node.left), IntervalNode.height(node.right))
			: -1;
	}

	hash() {
		return [this.eid, this.aid, this.low.toString(), this.high.toString(), this.status].join(',');
	}

	toString() {
		return `[${this.low},${this.high})`;
	}
}

/**
 * Unbalanced binary tree implementation.
 * Nodes must have { left, right, key } where left & right will be modified, and key used for placement
 */
export class UnbalancedBinTree {
	root;
	size = 0;

	add(node) {
		if (!this.root) {
			this.root = node;
			this.size = 1;
			return;
		}

		// Find a parent with an empty spot.
		let parent;
		let x = this.root;
		while (x) {
			parent = x;
			if (node.key < x.key) {
				x = x.left;
			} else {
				x = x.right;
			}
		}

		// Place node in parent.
		if (node.key < parent.key) {
			parent.left = node;
		} else {
			parent.right = node;
		}
		node.parent = parent;
		this.size += 1;
	}

	// delete node from the tree, and reorganise children, based on Cormen, intro to algo p.298
	delete(node) {
		this.size--;

		// Simple case when already missing a children, just hoist other children
		if (!node.left) return this.transplant(node, node.right);
		if (!node.right) return this.transplant(node, node.left);

		// find the next in-order node, as it's going to be our replacement (i.e. leftmost node of its right children).
		let nxt = node.right;
		while (nxt.left) nxt = nxt.left;

		// if not our direct right child,
		if (nxt.parent !== node) {
			// we know it doesn't have any left child
			this.transplant(nxt, nxt.right);
			// re-attach our right node, to it.
			nxt.right = node.right;
			nxt.right.parent = nxt;
		}

		// then re-attach our left node to it, and switch place.
		this.transplant(node, nxt);
		nxt.left = node.left;
		nxt.left.parent = nxt;
	}

	computeHeight() {
		return IntervalNode.height(this.root);
	}

	inOrder(fn) {
		this.root && inOrderTraversal(this.root, fn, 0);
	}

	// places nxt where cur was in the tree, disconnecting cur and its children, based on Cormen, intro to algo p.296
	transplant(cur, nxt) {
		if (cur === this.root) {
			this.root = nxt;
		} else if (cur === cur.parent.left) {
			cur.parent.left = nxt;
		} else {
			cur.parent.right = nxt;
		}

		// if nxt is also a node.
		if (nxt) {
			nxt.parent = cur.parent;
		}
	}
}

function inOrderTraversal(node, fn, depth) {
	if (!node) return;

	if (node.left) inOrderTraversal(node.left, fn, depth + 1);
	fn(node, depth);
	if (node.right) inOrderTraversal(node.right, fn, depth + 1);
}

/** State machine for a single stream that warns about state transition while walking batches of readings. */
export class StreamStateMachine {
	// Statuses are strongly valid for the duration of the bucket (either 1 min or granularity).
	// Statuses are weakly valid for OFFLINE AGE after the end of the bucket validity.
	// Note: Not knowing when observations are sent in a rollup period, we must assume it applies for the full period.

	start(threshold, timeoutInSec, rngCb, fromTs, toTs) {
		this.threshold = threshold;
		this.timeoutInSec = timeoutInSec;
		this.rngCb = rngCb;
		// Anything before truncate won't matter.
		this.leftTruncateTs = fromTs - timeoutInSec;
		this.fromTs = fromTs;
		this.toTs = toTs;

		// State variable.
		this.status = null; // Ongoing status (ie Alert, Warning, Normal)
		this.validUntil = Infinity; // Time at which this status expires, if no other observations are seen
		this.startedAt = null; // Time at which the ongoing status started to apply.
	}

	// Process a day's worth of reading.
	processDay(dayOfReadings) {
		const start = Date.parse(dayOfReadings.d) * 0.001;
		if (this.#shouldSkipDay(start)) {
			return;
		}

		this.#processRawDay(dayOfReadings);
	}

	// Close a potentially ongoing state
	end() {
		if (!this.status) return;

		const endTime = Math.min(this.validUntil, this.toTs);
		this.#changedStatus(null, endTime);
	}

	// This assumes there was a change in status.
	#changedStatus(newStatus, ts, dur) {

		// Need to collect status up to timeoutInSec before start, but don't need to warn about them.
		const postNewStatus = (from, to, status) => {
			if (from >= this.fromTs) {
				// Typical case, region in interval.
				this.rngCb(from, to, status);
			} else if (to > this.fromTs) {
				// Region overlaps with start of interval
				this.rngCb(this.fromTs, to, status);
			} //else do nothing region is before interval.

			this.status = null;
		}

		// new status
		if (!this.status) {
			this.status = newStatus;
			this.startedAt = ts;
			this.validUntil = ts + dur + this.timeoutInSec;
			return;
		}

		// closing an active status
		if (!newStatus) {
			postNewStatus(this.startedAt, ts, this.status);
			return;
		}

		// close old
		postNewStatus(this.startedAt, ts, this.status);

		// start new
		this.status = newStatus;
		this.startedAt = ts;
		this.validUntil = ts + dur + this.timeoutInSec;
	}

	// Keep inline with bulk
	#processRawDay({ t: times, v: values }) {
		const statuses = asStatus(this.threshold, values);
		const dur = MIN;
		for (let i = 0; i < times.length; i++) {
			const ts = times[i];
			if (ts < this.leftTruncateTs) continue;

			if (this.status) {
				// Interval has expired, stop here.
				if (this.toTs < ts) {
					this.#changedStatus(null, Math.min(this.validUntil, this.toTs));
					return;
				}

				// Region has expired, mark end and keep on.
				if (this.validUntil < ts) {
					this.#changedStatus(null, this.validUntil);
				}
			}

			const status = statuses[i];
			if (this.status !== status) {
				this.#changedStatus(status, ts, dur);
			} else {
				this.validUntil = ts + dur + this.timeoutInSec;
			}
		}
	}

	#shouldSkipDay(dayTs) {
		// Day of reading is either too early, or too late to impact our state.
		if ((dayTs + DAY - 1) < this.leftTruncateTs || dayTs > this.toTs) {
			if (this.status) {
				// We had an active status coming on to this (skipped) day.
				this.#changedStatus(null, this.toTs);
			}

			return true;
		}

		return false;
	}
}

// An optim to make status eval faster by simplifying its call. About ~5x faster for 90 days of data on Toronto.
function asStatus(threshold, values) {
	const la = threshold.lower?.alert;
	const ua = threshold.upper?.alert;
	const lw = threshold.lower?.warn;
	const uw = threshold.upper?.warn;

	const statuses = Array(values.length);
	for (let i = 0; i < values.length; i++) {
		const v = values[i];
		statuses[i] = doEvalSimpleState(la, ua, lw, uw, v, v);
	}
	return statuses;
}
